import { useState, useEffect } from 'react';
import axios from 'axios';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { 
  Users, 
  Calendar, 
  TestTubes, 
  TrendingUp,
  FileText,
  Download,
  Upload,
  RefreshCw,
  Clock,
  CheckCircle2,
  AlertCircle,
  Package,
  Wallet,
  Activity,
  ArrowUpRight,
  ArrowDownRight,
  BarChart3,
  PieChart
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart as RechartsPie, Pie, Cell } from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [recentBookings, setRecentBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reportType, setReportType] = useState('bookings');
  const [reportFormat, setReportFormat] = useState('csv');
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, bookingsRes] = await Promise.all([
        axios.get(`${API}/admin/stats`),
        axios.get(`${API}/bookings`)
      ]);
      setStats(statsRes.data);
      setRecentBookings(bookingsRes.data.slice(0, 10));
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadReport = async () => {
    setDownloading(true);
    try {
      const response = await axios.get(
        `${API}/admin/reports/${reportType}?format=${reportFormat}`,
        { responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${reportType}_report.${reportFormat}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`${reportType} report downloaded successfully`);
    } catch (error) {
      console.error('Error downloading report:', error);
      toast.error('Failed to download report');
    } finally {
      setDownloading(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-300',
      confirmed: 'bg-sky-100 text-sky-700 dark:bg-sky-900/50 dark:text-sky-300',
      completed: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-300',
      cancelled: 'bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300'
    };
    return <Badge className={styles[status] || styles.pending}>{status}</Badge>;
  };

  const statCards = stats ? [
    { 
      label: 'Total Users', 
      value: stats.total_users, 
      icon: Users, 
      color: 'bg-sky-500',
      bgColor: 'bg-sky-50 dark:bg-sky-900/30',
      trend: '+12%',
      trendUp: true
    },
    { 
      label: 'Total Bookings', 
      value: stats.total_bookings, 
      icon: Calendar, 
      color: 'bg-purple-500',
      bgColor: 'bg-purple-50 dark:bg-purple-900/30',
      trend: '+8%',
      trendUp: true
    },
    { 
      label: 'Active Tests', 
      value: stats.total_tests, 
      icon: TestTubes, 
      color: 'bg-emerald-500',
      bgColor: 'bg-emerald-50 dark:bg-emerald-900/30',
      trend: '0%',
      trendUp: true
    },
    { 
      label: 'Total Revenue', 
      value: `₦${stats.total_revenue?.toLocaleString('en-US', { minimumFractionDigits: 2 }) || '0.00'}`, 
      icon: Wallet, 
      color: 'bg-amber-500',
      bgColor: 'bg-amber-50 dark:bg-amber-900/30',
      trend: '+15%',
      trendUp: true
    },
  ] : [];

  const bookingStatusData = stats ? [
    { name: 'Pending', value: stats.pending_bookings, fill: '#F59E0B' },
    { name: 'Confirmed', value: stats.confirmed_bookings, fill: '#0EA5E9' },
    { name: 'Completed', value: stats.completed_bookings, fill: '#10B981' },
    { name: 'Awaiting Payment', value: stats.awaiting_payment || 0, fill: '#8B5CF6' }
  ] : [];

  const COLORS = ['#F59E0B', '#0EA5E9', '#10B981', '#8B5CF6'];

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex justify-center items-center h-96">
          <div className="spinner" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="admin-dashboard">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Admin Dashboard</h1>
            <p className="text-muted-foreground mt-1">Overview of your diagnostic centre</p>
          </div>
          <Button 
            variant="outline" 
            onClick={fetchData}
            className="flex items-center gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh Data
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {statCards.map((stat, index) => (
            <div 
              key={index} 
              className="card-clinical p-5 hover:shadow-md transition-shadow"
              data-testid={`stat-card-${stat.label.toLowerCase().replace(' ', '-')}`}
            >
              <div className="flex items-center justify-between mb-4">
                <div className={`w-12 h-12 ${stat.bgColor} rounded-xl flex items-center justify-center`}>
                  <stat.icon className={`w-6 h-6 text-${stat.color.replace('bg-', '')}`} style={{color: stat.color.replace('bg-', '#').replace('-500', '')}} />
                </div>
                <div className={`flex items-center gap-1 text-xs ${stat.trendUp ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}>
                  {stat.trendUp ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
                  {stat.trend}
                </div>
              </div>
              <p className="text-2xl font-bold text-foreground">{stat.value}</p>
              <p className="text-sm text-muted-foreground mt-1">{stat.label}</p>
            </div>
          ))}
        </div>

        {/* Quick Status Cards */}
        <div className="grid sm:grid-cols-3 gap-4">
          <div className="card-clinical p-4 bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800">
            <div className="flex items-center gap-3">
              <Clock className="w-8 h-8 text-amber-600" />
              <div>
                <p className="text-2xl font-bold text-amber-700 dark:text-amber-300">{stats?.pending_bookings || 0}</p>
                <p className="text-sm text-amber-600 dark:text-amber-400">Pending Bookings</p>
              </div>
            </div>
          </div>
          <div className="card-clinical p-4 bg-purple-50 dark:bg-purple-900/20 border-purple-200 dark:border-purple-800">
            <div className="flex items-center gap-3">
              <AlertCircle className="w-8 h-8 text-purple-600" />
              <div>
                <p className="text-2xl font-bold text-purple-700 dark:text-purple-300">{stats?.awaiting_payment || 0}</p>
                <p className="text-sm text-purple-600 dark:text-purple-400">Awaiting Payment</p>
              </div>
            </div>
          </div>
          <div className="card-clinical p-4 bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800">
            <div className="flex items-center gap-3">
              <CheckCircle2 className="w-8 h-8 text-emerald-600" />
              <div>
                <p className="text-2xl font-bold text-emerald-700 dark:text-emerald-300">{stats?.completed_bookings || 0}</p>
                <p className="text-sm text-emerald-600 dark:text-emerald-400">Completed Tests</p>
              </div>
            </div>
          </div>
        </div>

        {/* Charts Row */}
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Booking Status Chart */}
          <div className="card-clinical p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold text-foreground flex items-center gap-2">
                <PieChart className="w-5 h-5 text-sky-600" />
                Booking Status
              </h2>
            </div>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <RechartsPie>
                  <Pie
                    data={bookingStatusData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={80}
                    paddingAngle={5}
                    dataKey="value"
                  >
                    {bookingStatusData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </RechartsPie>
              </ResponsiveContainer>
            </div>
            <div className="flex flex-wrap justify-center gap-4 mt-4">
              {bookingStatusData.map((item, index) => (
                <div key={index} className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[index] }} />
                  <span className="text-sm text-muted-foreground">{item.name}: {item.value}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Reports Download Section */}
          <div className="card-clinical p-6">
            <h2 className="text-lg font-semibold text-foreground flex items-center gap-2 mb-6">
              <BarChart3 className="w-5 h-5 text-sky-600" />
              Generate Reports
            </h2>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-foreground mb-2 block">Report Type</label>
                <Select value={reportType} onValueChange={setReportType}>
                  <SelectTrigger data-testid="report-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="users">Users Report</SelectItem>
                    <SelectItem value="bookings">Bookings Report</SelectItem>
                    <SelectItem value="results">Lab Results Report</SelectItem>
                    <SelectItem value="revenue">Revenue Report</SelectItem>
                    <SelectItem value="inventory">Inventory Report</SelectItem>
                    <SelectItem value="tests">Test Price List</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-foreground mb-2 block">Format</label>
                <Select value={reportFormat} onValueChange={setReportFormat}>
                  <SelectTrigger data-testid="report-format-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="csv">CSV (Excel)</SelectItem>
                    <SelectItem value="pdf">PDF Document</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button 
                className="w-full bg-sky-600 hover:bg-sky-700"
                onClick={handleDownloadReport}
                disabled={downloading}
                data-testid="download-report-btn"
              >
                {downloading ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4 mr-2" />
                    Download Report
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>

        {/* Recent Bookings */}
        <div className="card-clinical">
          <div className="p-6 border-b border-border">
            <h2 className="text-lg font-semibold text-foreground flex items-center gap-2">
              <Activity className="w-5 h-5 text-sky-600" />
              Recent Bookings
            </h2>
          </div>
          {recentBookings.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              No bookings yet
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left py-3 px-4 font-medium text-muted-foreground">ID</th>
                    <th className="text-left py-3 px-4 font-medium text-muted-foreground">Date</th>
                    <th className="text-left py-3 px-4 font-medium text-muted-foreground">Type</th>
                    <th className="text-left py-3 px-4 font-medium text-muted-foreground">Amount</th>
                    <th className="text-left py-3 px-4 font-medium text-muted-foreground">Status</th>
                    <th className="text-left py-3 px-4 font-medium text-muted-foreground">Payment</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {recentBookings.map((booking) => (
                    <tr key={booking.id} className="hover:bg-muted/30">
                      <td className="py-3 px-4 font-mono text-sm text-foreground">{booking.id?.slice(0, 8)}</td>
                      <td className="py-3 px-4 text-foreground">{booking.appointment_date}</td>
                      <td className="py-3 px-4 capitalize text-foreground">{booking.booking_type?.replace('_', ' ')}</td>
                      <td className="py-3 px-4 font-medium text-foreground">₦{booking.total_amount?.toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                      <td className="py-3 px-4">{getStatusBadge(booking.status)}</td>
                      <td className="py-3 px-4">{getStatusBadge(booking.payment_status)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default AdminDashboard;
